import { defineStore } from "pinia";
import { computed, nextTick, reactive, watch } from "vue";
import type {
    ProductSearchFilterParams
} from "@/api/modules/autoQ/productSearch/types.ts";
import isEqual from "lodash/isEqual";
import { StorageHelper } from "@/utils/StorageHelper";
import { StorageName } from "@/utils/StorageHelper/constants";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";

export const useProductSearchFilterParamsStore = defineStore("productSearchFilterParamsStore", () => {

    const {
        write,
        read,
        remove
    } = new StorageHelper(StorageName.ProductSearchFilterParamsStore);

    const initialValues = {
        productName: [],
        productNumber: [],
        productObjectId: [],
        productProperty: [],
        productTitle: [],
        appearance: [],
        channelName: [],
        brandName: [],
        contaminants: [],
        divisionName: [],
        divisionId: [],
        nutritionStatements: [],
        productAvailability: [],
        technicalDataSheets: [],
        purchasingSpecifications: [],
        purities: [],
        qualityStatements: [],
        supplierCertificates: [],
        supplierStatements: [],
        saltName: [],
        metalName: [],
        typeOfCertificates: [],
        suppliers: [],
        fulltext: "",
        productCategories: [],
        productApplications: []
    };

    const filterParams = reactive<ProductSearchFilterParams>({});

    const reset = async () => {
        Object.assign(filterParams, cloneDeep(initialValues));

        await nextTick();

        remove();

        return Promise.resolve();
    };

    const changeValue = (propertyName: string, value: string[] | undefined | string) => {
        filterParams[propertyName] = value;
    };

    const hasChanges = computed(() => !isEqual({ ...initialValues }, filterParams));

    const initialize = async () => {
        const storedFilterParams = <ProductSearchFilterParams>read();

        Object.assign(filterParams, cloneDeep(initialValues));

        if (!storedFilterParams) return;

        Object.assign(filterParams, storedFilterParams);

        return Promise.resolve();
    };

    const saveFilterParamsToStore = () => {
        write(filterParams);
    };

    const activeFiltersBase = computed(() => Object.entries(filterParams).filter((item) => !isEmpty(item[1])));

    const activeFilters = computed(() => activeFiltersBase.value.filter((item) => Array.isArray(item[1])));
    const activeFiltersText = computed(() => activeFiltersBase.value.filter((item) => !Array.isArray(item[1])));
    const hasActiveFilters = computed(() => !!activeFiltersText.value.length || !!activeFilters.value.length);

    watch(filterParams, () => {
        saveFilterParamsToStore();
    }, { deep: true });


    return {
        filterParams,
        reset,
        changeValue,
        hasChanges,
        initialValues,
        initialize,
        activeFilters,
        activeFiltersText,
        hasActiveFilters
    };
});